import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postReturnForm } from '@yme/api';
import { BrandOverline } from '@yme/atoms/brand-overline';
import { Col, Row } from '@yme/atoms/grid';
import { DATE_FORMAT } from '@yme/molecules/date-picker/constants';
import { getCurrencyRate, getInitialCountryRoot } from '@yme/selectors/common';
import { getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300, colorCoreBlack900Base, colorCoreWhite1000 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularLinkLevel1, RegularTextLevel3 } from '@yme/tokens/typography';
import { DigitalDataMutation, setDigitalData, normalizeString } from '@yme/utils/common';
import { GB_COUNTRY_ROOT } from '@yme/utils/constants';
import { formatPrice } from '@yme/utils/formatPrice';
import { ButtonsNavBar, InfoTable, Reimbursement, ReturnedItemsList } from '../components';
import { FormReturnType, FormikFields, StepRoutes } from '../constants';
import { addDataToReturnedFormItems, getEurosFromCentAmount, getParcelNumberByOrder, getReimbursement, } from '../helpers';
import { ContentContainerStyled, FlexWrapperStyled, InfoSectionContainerStyled } from './styled';
const transformItemsForRequst = (items, locale) => map(items, (item) => ({
    ...item,
    price: formatPrice({
        locale,
        currencyName: item.price.currencyCode,
        price: getEurosFromCentAmount(item.price.centAmount * item.returnQuantity),
    }),
}));
export const ShippingStep = memo(({ returnType, stepData: { dpdLink }, cachedData: { orderDetails, orderDetails: { orderEntries, shippingCost, deliveries: { 0: { parcels }, }, }, returnedFormItems, parcelNumber, pickupDate, pickupAddress, pickupPostalCode, pickupPlace, }, pagePath, returnReasonsEn, setCachedData, }) => {
    const { t, language } = useTranslation();
    const history = useHistory();
    const isRightToLeft = useSelector(getRTL);
    const currencyRate = useSelector(getCurrencyRate);
    const initialCountryRoot = useSelector(getInitialCountryRoot);
    const isGBCountyRoot = initialCountryRoot === GB_COUNTRY_ROOT;
    const isTracked = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const returnedItems = useMemo(() => addDataToReturnedFormItems(filter(returnedFormItems, `${FormikFields.RETURN_QUANTITY}`), orderEntries), [orderEntries, returnedFormItems]);
    const analyticData = useMemo(() => {
        if (currencyRate) {
            const { rate } = currencyRate;
            const returnItemsQuantity = returnedItems.reduce((quantity, item) => {
                return quantity + item.returnQuantity;
            }, 0);
            const products = returnedItems.map((item) => {
                const returnReason = item.returnReasonOther
                    ? normalizeString(item.returnReasonOther)
                    : returnReasonsEn
                        ? normalizeString(returnReasonsEn[item.returnReasonCode || '1'])
                        : '';
                const unitPrice = parseFloat((getEurosFromCentAmount(item.price.centAmount) / rate).toFixed(2));
                return {
                    name: normalizeString(item.nameEN),
                    id: normalizeString(item.slug),
                    skuCode: normalizeString(item.sku),
                    category: normalizeString(item?.accessoryType),
                    unitPrice,
                    unitSalePrice: unitPrice,
                    quantity: item.returnQuantity,
                    returnReason,
                };
            });
            return {
                returnItemsQuantity,
                products,
            };
        }
        return null;
    }, [currencyRate, returnReasonsEn, returnedItems]);
    useEffect(() => {
        if (analyticData?.returnItemsQuantity && !isTracked.current && currencyRate) {
            isTracked.current = true;
            setDigitalData(DigitalDataMutation.RETURN_PAGE_REQUEST_VIEW, analyticData, currencyRate);
        }
    }, [analyticData, currencyRate]);
    const handleSubmit = useCallback(async () => {
        setIsLoading(true);
        try {
            const { 
            // ready
            id, orderNumber, firstName, lastName, phone, email, country, countryCode, city, postalCode, streetName, locale, additionalStreetInfo, state, 
            // to transform
            shippingCost, } = orderDetails;
            const formattedShippingCost = formatPrice({
                locale: language,
                currencyName: shippingCost.currencyCode,
                price: getEurosFromCentAmount(shippingCost.centAmount),
            });
            const { total } = getReimbursement(parcels, returnedItems, shippingCost);
            const totalReturnPrice = formatPrice({
                locale: language,
                currencyName: shippingCost.currencyCode,
                price: total,
            });
            const body = {
                // order
                orderNumber,
                firstName,
                lastName,
                phone,
                email,
                country,
                countryCode,
                state,
                city,
                postalCode,
                streetName,
                additionalStreetInfo,
                pagePath,
                locale: isGBCountyRoot ? locale.toLowerCase() : locale,
                orderEntries: transformItemsForRequst(returnedItems, language),
                ...(!isGBCountyRoot && {
                    id,
                    // model
                    returnType,
                    formattedShippingCost,
                    // inputs
                    parcelNumber,
                    ...(returnType === FormReturnType.COLLECTION && {
                        pickupDate: format(pickupDate, DATE_FORMAT['yyyy/MM/dd']),
                        pickupStreetName: pickupAddress,
                        pickupPostalCode,
                        pickupCity: pickupPlace,
                    }),
                    // calculated
                    totalReturnPrice,
                }),
            };
            const response = await postReturnForm({
                body,
                isSupportNotifyEndpoint: isGBCountyRoot,
            });
            if (response) {
                if (analyticData && currencyRate) {
                    setDigitalData(DigitalDataMutation.RETURN_PAGE_REQUEST_SUBMISSION_VIEW, analyticData, currencyRate);
                }
                setCachedData((prevCachedData) => ({
                    ...prevCachedData,
                    pdfFile: !isGBCountyRoot ? Buffer.from(response).toJSON().data : undefined,
                }));
                history.push(`/${StepRoutes.END_STEP_SUCCESS}`);
            }
        }
        catch (error) {
            history.push(`/${StepRoutes.END_STEP_FAIL}`);
        }
        finally {
            setIsLoading(false);
        }
    }, [
        currencyRate,
        analyticData,
        orderDetails,
        language,
        parcels,
        returnedItems,
        isGBCountyRoot,
        returnType,
        pagePath,
        parcelNumber,
        pickupDate,
        pickupAddress,
        pickupPostalCode,
        pickupPlace,
        setCachedData,
        history,
    ]);
    return (React.createElement(FlexWrapperStyled, { flexDirection: "column" },
        React.createElement(ContentContainerStyled, { width: "100%" },
            React.createElement(Row, { justifyContent: "center" },
                React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                    React.createElement(BrandOverline, { borderSize: "6px", borderColor: colorCoreBlack900Base },
                        React.createElement(InfoSectionContainerStyled, null,
                            React.createElement(HeadingLevel3, null, t('returnForm_shippingLabelRegisterReturn_header')),
                            React.createElement(FlexWrapper, { mt: "8px" },
                                React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_shippingLabelRegisterReturn_description'))),
                            React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                React.createElement(InfoTable, { ...orderDetails, parcelNumber: getParcelNumberByOrder(orderDetails), isRightToLeft: isRightToLeft }))))))),
        React.createElement(ContentContainerStyled, { width: "100%" },
            React.createElement(Row, { justifyContent: "center" },
                React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                    React.createElement(InfoSectionContainerStyled, null,
                        React.createElement(HeadingLevel3, null, t('returnForm_shippingLabelReturnItems_header')),
                        React.createElement(FlexWrapper, { mt: "8px" },
                            React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_shippingLabelReturnItems_description'))),
                        React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                            React.createElement(ReturnedItemsList, { items: returnedItems, isReadOnly: true }),
                            React.createElement(Reimbursement, { items: returnedItems, shippingCost: shippingCost, parcels: parcels })))))),
        React.createElement(ContentContainerStyled, { width: "100%" },
            React.createElement(Row, { justifyContent: "center" },
                React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                    React.createElement(InfoSectionContainerStyled, null,
                        React.createElement(HeadingLevel3, null, t('returnForm_shippingLabelInstructions_header')),
                        React.createElement(FlexWrapper, { mt: "8px" },
                            React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_shippingLabelInstructions_description'))),
                        React.createElement(FlexWrapper, { mt: "32px" },
                            React.createElement(RegularTextLevel3, null,
                                t('returnForm_shippingLabelInstructions_text'),
                                ' ',
                                Boolean(dpdLink) && (React.createElement(RegularLinkLevel1, { href: dpdLink }, t('returnForm_shippingLabelInstructions_link'))))))))),
        React.createElement(ButtonsNavBar, { isNextDisabled: false, forwardButtonLabel: t('returnForm_shippingLabelCreateLabel_button'), onForwardPress: handleSubmit, isLoading: isLoading })));
});
ShippingStep.displayName = 'YourShippingStep_ReturnForm';
